import { useEffect, useState } from "react";
import {
  getCurrentLegalDoc,
  getLegalDocVersions,
  publishLegalDoc,
} from "@shared/api/adminApi.ts";
import { ApiError } from "@shared/api/http.ts";
import { Alert, Button } from "@shared/components/Button.tsx";
import { Markdown } from "@shared/components/Markdown.tsx";
import { Modal } from "@shared/components/Modal.tsx";
import {
  Loading,
  Skeleton,
  SkeletonText,
} from "@shared/components/Skeleton.tsx";
import { useToast } from "@shared/components/Toast.tsx";
import { formatDateTimeAdmin } from "@shared/lib/format.ts";
import { useAsync } from "@shared/lib/useAsync.ts";
import type { LegalDocument } from "@shared/types.ts";
import { AdminBanner } from "../Layout.tsx";

const STARTER = `# शिव-वक्ता स्पर्धा २०२६-२७ — अटी व नियम

## सर्वसाधारण अटी

1.

## स्पर्धेचे नियम

1. `;

export function LegalPage() {
  const toast = useToast();
  const current = useAsync(getCurrentLegalDoc, []);
  const versions = useAsync(getLegalDocVersions, []);
  const [selected, setSelected] = useState<number | null>(null); // version number being viewed
  const [editing, setEditing] = useState<string | null>(null); // draft content when editor open

  const all = [...(versions.data ?? [])].sort((a, b) => b.version - a.version);
  const shown: LegalDocument | null | undefined =
    selected === null ? current.data : all.find((v) => v.version === selected);

  const reloadAll = () => {
    current.reload();
    versions.reload();
  };

  return (
    <>
      <AdminBanner
        title="माहिती, नियम आणि अटी"
        sub='हा दस्तऐवज सार्वजनिक संकेतस्थळावरील "माहिती, नियम आणि अटी" पानावर व नोंदणीच्या पहिल्या पायरीवर दाखवला जातो.'
        actions={
          <Button
            icon="edit"
            disabled={current.status === "loading"}
            onClick={() => setEditing(current.data?.content ?? STARTER)}
          >
            {current.data
              ? "नवीन आवृत्ती तयार करा"
              : "पहिली आवृत्ती प्रकाशित करा"}
          </Button>
        }
      />
      <div className="admin-container admin-content legal-grid">
        <section className="panel">
          <header className="panel__head">
            <h2>
              {selected === null
                ? "सध्याची प्रकाशित आवृत्ती"
                : `आवृत्ती ${selected}`}
              {shown && (
                <span className="chip chip-muted" style={{ marginLeft: 10 }}>
                  v{shown.version}
                  {shown.is_current ? " · सध्याची" : ""}
                </span>
              )}
            </h2>
            {selected !== null && shown && !shown.is_current && (
              <div className="row-gap">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setSelected(null)}
                >
                  सध्याची पाहा
                </Button>
                <Button
                  size="sm"
                  icon="history"
                  onClick={() => setEditing(shown.content)}
                >
                  ही आवृत्ती पुन्हा प्रकाशित करा
                </Button>
              </div>
            )}
          </header>

          {current.status === "loading" &&
          !current.data &&
          selected === null ? (
            <Loading label="दस्तऐवज लोड होत आहे…">
              <Skeleton w="55%" h={26} style={{ marginBottom: 18 }} />
              <SkeletonText lines={4} />
              <Skeleton w="35%" h={20} style={{ margin: "24px 0 12px" }} />
              <SkeletonText lines={5} />
            </Loading>
          ) : current.status === "error" && selected === null ? (
            <Alert
              tone="error"
              title="दस्तऐवज लोड होऊ शकला नाही"
              action={
                <Button size="sm" variant="secondary" onClick={current.reload}>
                  पुन्हा
                </Button>
              }
            />
          ) : !shown ? (
            <div className="empty-state">
              <h2>अद्याप दस्तऐवज प्रकाशित झालेला नाही</h2>
              <p>
                प्रकाशित होईपर्यंत सार्वजनिक संकेतस्थळावर तात्पुरता मजकूर दिसतो
                (सर्व्हर या स्थितीत त्रुटी परत करतो).
              </p>
            </div>
          ) : (
            <>
              <p className="muted small">
                प्रकाशित: {formatDateTimeAdmin(shown.created_at)}
              </p>
              <div className="doc-preview">
                <Markdown source={shown.content} />
              </div>
            </>
          )}
        </section>

        <aside className="panel">
          <header className="panel__head">
            <h2>आवृत्त्यांचा इतिहास</h2>
          </header>
          {versions.status === "loading" && !versions.data ? (
            <Loading>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} h={52} style={{ marginBottom: 8 }} />
              ))}
            </Loading>
          ) : versions.status === "error" ? (
            <Alert
              tone="error"
              title="इतिहास लोड होऊ शकला नाही"
              action={
                <Button size="sm" variant="secondary" onClick={versions.reload}>
                  पुन्हा
                </Button>
              }
            />
          ) : all.length === 0 ? (
            <p className="muted">अद्याप एकही आवृत्ती नाही.</p>
          ) : (
            <ul className="versions">
              {all.map((v) => {
                const on =
                  selected === null ? v.is_current : selected === v.version;
                return (
                  <li key={v.id}>
                    <button
                      type="button"
                      className={`version ${on ? "is-on" : ""}`}
                      onClick={() =>
                        setSelected(v.is_current ? null : v.version)
                      }
                    >
                      <strong>आवृत्ती {v.version}</strong>
                      {v.is_current && (
                        <span className="chip chip-ok">सध्याची</span>
                      )}
                      <span className="muted num">
                        {formatDateTimeAdmin(v.created_at)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <p className="muted small legal-note">
            दस्तऐवज थेट बदलता किंवा हटवता येत नाही — प्रत्येक बदल नवीन आवृत्ती
            म्हणून प्रकाशित होतो व जुन्या आवृत्त्या इतिहासात राहतात.
          </p>
        </aside>
      </div>

      <Editor
        draft={editing}
        nextVersion={(all[0]?.version ?? 0) + 1}
        onClose={() => setEditing(null)}
        onPublished={(v) => {
          setEditing(null);
          setSelected(null);
          toast(
            "success",
            `आवृत्ती ${v} प्रकाशित झाली. सार्वजनिक संकेतस्थळावर आता हीच दिसेल.`,
          );
          reloadAll();
        }}
      />
    </>
  );
}

function Editor({
  draft,
  nextVersion,
  onClose,
  onPublished,
}: {
  draft: string | null;
  nextVersion: number;
  onClose: () => void;
  onPublished: (version: number) => void;
}) {
  const [text, setText] = useState("");
  const [tab, setTab] = useState<"write" | "preview">("write");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (draft !== null) {
      setText(draft);
      setTab("write");
      setConfirming(false);
      setError(undefined);
    }
  }, [draft]);

  const publish = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const { version } = await publishLegalDoc(text);
      onPublished(version);
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 409
          ? "त्याच वेळी दुसऱ्या प्रशासकाने आवृत्ती प्रकाशित केली. पुन्हा प्रयत्न करा."
          : e instanceof ApiError
            ? e.message
            : "प्रकाशित होऊ शकले नाही.",
      );
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  const empty = !text.trim();

  return (
    <Modal
      open={draft !== null}
      onClose={() => !busy && onClose()}
      size="lg"
      title={`नवीन आवृत्ती (v${nextVersion})`}
      footer={
        confirming ? (
          <>
            <span className="mr-auto muted">
              प्रकाशित केल्यावर सर्व स्पर्धकांना लगेच हीच आवृत्ती दिसेल.
            </span>
            <Button
              variant="secondary"
              onClick={() => setConfirming(false)}
              disabled={busy}
            >
              मागे
            </Button>
            <Button
              icon="check"
              onClick={publish}
              loading={busy}
              loadingText="प्रकाशित होत आहे…"
            >
              होय, प्रकाशित करा
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              रद्द करा
            </Button>
            <Button onClick={() => setConfirming(true)} disabled={empty}>
              प्रकाशित करा…
            </Button>
          </>
        )
      }
    >
      <div className="stack">
        {error && <Alert tone="error">{error}</Alert>}
        <div className="seg seg--small" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "write"}
            className={`seg-btn ${tab === "write" ? "is-on" : ""}`}
            onClick={() => setTab("write")}
          >
            लिहा
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "preview"}
            className={`seg-btn ${tab === "preview" ? "is-on" : ""}`}
            onClick={() => setTab("preview")}
          >
            पूर्वावलोकन
          </button>
        </div>
        {tab === "write" ? (
          <>
            <textarea
              id="legal_editor"
              className="input editor"
              value={text}
              onChange={(e) => setText(e.target.value)}
              spellCheck={false}
              aria-label="दस्तऐवज मजकूर (Markdown)"
            />
            <p className="muted small">
              Markdown वापरा: <code># शीर्षक</code>, <code>## उपशीर्षक</code>,{" "}
              <code>1. क्रमांकित मुद्दा</code>, <code>**ठळक**</code>,{" "}
              <code>&gt; सूचना</code>.
            </p>
          </>
        ) : (
          <div className="doc-preview doc-preview--editor">
            {empty ? (
              <p className="muted">मजकूर रिकामा आहे.</p>
            ) : (
              <Markdown source={text} />
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
